/**
 * A group chat's details: its name and topic, who is in it, and what each
 * person can do about it.
 *
 * What is offered is `powers()`. Any member can add somebody, by username or
 * email address and nothing else, and can leave; the owner alone can rename,
 * set the topic, remove someone and delete the chat for everyone. The server
 * enforces every one of those, so a refusal — an organisation that does not
 * admit that person, a block, a full room — comes back as a sentence under the
 * controls and never as a button that stays pressed. Remove, leave and delete
 * each ask first. A direct chat has none of this and never opens the dialog.
 */

import { useState } from 'react';
import { ds } from '../../ds';
import { useT } from '../../i18n';
import { store, useStore } from '../../lib/relay/store';
import { Avatar } from '../Avatar';
import { findPerson, powers, problemOf } from './groups';
import type { Conversation, Person } from '../../lib/relay/types';
import './chat.css';

/** One write at a time, and what became of it: the server's sentence when it
 *  refused, a plain one when the request never got there. */
function useAttempt() {
  const t = useT();
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  const clear = () => setProblem('');
  const attempt = async (action: () => Promise<void>): Promise<boolean> => {
    setBusy(true);
    setProblem('');
    try {
      await action();
      return true;
    } catch (err) {
      setProblem(problemOf(err) ?? t('common.problem'));
      return false;
    } finally {
      setBusy(false);
    }
  };
  return { busy, problem, attempt, clear };
}

function Problem({ children }: { children: string }) {
  return (
    <div role="alert" style={{ fontSize: 13, color: 'var(--danger-500)' }}>
      {children}
    </div>
  );
}

function MemberRow({
  person,
  you,
  owner,
  onRemove
}: {
  person: Person;
  you: boolean;
  owner: boolean;
  onRemove?: () => void;
}) {
  const { Badge, IconButton } = ds();
  const t = useT();
  const presence = useStore((s) => s.presenceOf(person.id));
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-10)' }}>
      <Avatar person={person} size={28} presence={presence} />
      <span style={{ flex: 1, minWidth: 0, display: 'grid' }}>
        <span className="rl-truncate">{you ? t('common.you') : person.name}</span>
        <span className="rl-truncate" style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          {t(`conversation.presence.${presence}`)}
        </span>
      </span>
      {owner && <Badge tone="neutral">{t('conversation.members.owner')}</Badge>}
      {onRemove && (
        <IconButton
          icon="Close"
          size="sm"
          label={t('conversation.members.remove', { name: person.name })}
          onClick={onRemove}
        />
      )}
    </div>
  );
}

/** A step that cannot be taken back, asked before it is taken. Cancel is always
 *  there, so a refusal never leaves anyone with nothing to press. */
function Confirm({
  title,
  body,
  label,
  action,
  onDone,
  onClose
}: {
  title: string;
  body?: string;
  label: string;
  action: () => Promise<void>;
  onDone: () => void;
  onClose: () => void;
}) {
  const { Button, Dialog } = ds();
  const t = useT();
  const { busy, problem, attempt } = useAttempt();
  return (
    <Dialog
      open
      position="fixed"
      title={title}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="danger"
            disabled={busy}
            onClick={() => void attempt(action).then((ok) => ok && onDone())}
          >
            {label}
          </Button>
        </>
      }
    >
      {body && <p>{body}</p>}
      {problem && <Problem>{problem}</Problem>}
    </Dialog>
  );
}

type Asking = { step: 'remove'; person: Person } | { step: 'leave' } | { step: 'delete' };

export function MembersDialog({
  conversation,
  onClose,
  onLeft,
  onDeleted
}: {
  conversation: Conversation;
  onClose: () => void;
  /** Called after leaving, so the page can go back to the list. */
  onLeft: () => void;
  /** Called after the owner deletes the conversation for everyone, so the
   *  page can go back to the list. */
  onDeleted: () => void;
}) {
  const { Button, Dialog, Input } = ds();
  const t = useT();
  const me = useStore((s) => s.me());
  const members = useStore((s) => s.people(conversation.members));

  const [who, setWho] = useState('');
  const [whoError, setWhoError] = useState('');
  const [title, setTitle] = useState(conversation.title);
  const [topic, setTopic] = useState(conversation.topic);
  const [asking, setAsking] = useState<Asking | null>(null);
  const { busy, problem, attempt, clear } = useAttempt();

  const can = powers(conversation, me?.id);
  const changed =
    title.trim() !== conversation.title || topic.trim() !== conversation.topic;

  const save = () =>
    void attempt(() => store.rename(conversation.id, title.trim(), topic.trim()));

  const add = () => {
    const typed = who.trim();
    if (!typed || busy) return;
    setWhoError('');
    void attempt(async () => {
      const found = await findPerson(store, typed);
      if ('refusal' in found) {
        setWhoError(
          found.refusal === 'self' ? t('conversation.members.self') : t('conversation.members.notfound')
        );
        return;
      }
      if (!conversation.members.includes(found.person.id)) {
        await store.addMember(conversation.id, found.person.id);
      }
      setWho('');
    });
  };

  const ask = (next: Asking) => {
    clear();
    setAsking(next);
  };

  const confirm = () => {
    if (!asking) return null;
    const close = () => setAsking(null);
    if (asking.step === 'remove') {
      const { person } = asking;
      return (
        <Confirm
          title={t('conversation.members.remove.confirm', { name: person.name })}
          body={t('conversation.members.remove.confirm.body')}
          label={t('conversation.members.remove.action')}
          action={() => store.removeMember(conversation.id, person.id)}
          onDone={close}
          onClose={close}
        />
      );
    }
    if (asking.step === 'leave') {
      return (
        <Confirm
          title={t('conversation.members.leave.confirm', { title: conversation.title })}
          body={
            can.dissolve
              ? t('conversation.members.leave.confirm.owner')
              : t('conversation.members.leave.confirm.body')
          }
          label={t('conversation.members.leave.action')}
          action={() => store.leave(conversation.id)}
          onDone={() => {
            onClose();
            onLeft();
          }}
          onClose={close}
        />
      );
    }
    return (
      <Confirm
        title={t('conversation.members.delete.confirm', { title: conversation.title })}
        body={t('conversation.members.delete.confirm.body')}
        label={t('conversation.members.delete')}
        action={() => store.deleteForEveryone(conversation.id)}
        onDone={() => {
          onClose();
          onDeleted();
        }}
        onClose={close}
      />
    );
  };

  return (
    <>
      <Dialog open position="fixed" title={t('conversation.members.title')} onClose={onClose}>
        {can.rename && (
          <div style={{ display: 'grid', gap: 'var(--space-12)', marginBottom: 'var(--space-16)' }}>
            <Input
              label={t('conversation.name')}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            <Input
              label={t('conversation.topic')}
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
            />
            <div>
              <Button size="sm" disabled={busy || !changed || !title.trim()} onClick={save}>
                {t('common.save')}
              </Button>
            </div>
          </div>
        )}

        <div style={{ display: 'grid', gap: 'var(--space-8)' }}>
          {members.map((person) => (
            <MemberRow
              key={person.id}
              person={person}
              you={person.id === me?.id}
              owner={person.id === conversation.ownerId}
              {...(can.remove && person.id !== me?.id
                ? { onRemove: () => ask({ step: 'remove', person }) }
                : {})}
            />
          ))}
        </div>

        {can.add && (
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: 'var(--space-8)',
              marginTop: 'var(--space-16)'
            }}
          >
            <Input
              label={t('conversation.members.find')}
              value={who}
              autoComplete="off"
              error={whoError || undefined}
              style={{ flex: 1 }}
              onChange={(e) => {
                setWho(e.target.value);
                setWhoError('');
              }}
              onKeyDown={(e) => {
                if (e.key !== 'Enter' || e.nativeEvent.isComposing) return;
                e.preventDefault();
                add();
              }}
            />
            <Button variant="secondary" disabled={busy || !who.trim()} onClick={add}>
              {t('common.add')}
            </Button>
          </div>
        )}

        {problem && (
          <div style={{ marginTop: 'var(--space-12)' }}>
            <Problem>{problem}</Problem>
          </div>
        )}

        <div
          style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-8)', marginTop: 'var(--space-20)' }}
        >
          {can.leave && (
            <Button variant="danger" onClick={() => ask({ step: 'leave' })}>
              {t('conversation.members.leave')}
            </Button>
          )}
          {can.dissolve && (
            <Button variant="danger" onClick={() => ask({ step: 'delete' })}>
              {t('conversation.members.delete')}
            </Button>
          )}
        </div>
      </Dialog>

      {confirm()}
    </>
  );
}
